import { addNotification } from "portal/state/notifications";
import {
  arePlantProfilesEqual,
  UnsavedPlantProfile,
} from "portal/utils/categoryCollection/plantProfile";
import { capitalize } from "@mui/material";
import { CLIENT_ERROR_CODES } from "portal/utils/errors";
import {
  GetPredictionPointSessionResponse,
  SessionStatus,
} from "protos/veselka/prediction_point";
import { isEmpty } from "common/utils/arrays";
import { isEqual } from "portal/utils/equality";
import { portalApi } from "portal/state/portalApi";
import {
  PredictionPointApiBaseFilters,
  toApiCategoryCollectionProfile,
  UiPredictionPointFilters,
} from "portal/utils/categoryCollection/predictionPoints";
import { skipToken } from "@reduxjs/toolkit/query";
import { t } from "i18next";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useToPredictionPointFilters } from "./filters/useToPredictionPointFilters";

interface Session {
  id?: string;
  status?: SessionStatus;
  error: boolean;
  stale: boolean;
  filters?: PredictionPointApiBaseFilters;
}
const INITIAL_STATE = {
  id: undefined,
  status: undefined,
  error: false,
  stale: false,
  filters: undefined,
};

const SESSION_POLL_INTERVAL_MS = 1000;

export const usePreviewResults = (
  plantProfile?: UnsavedPlantProfile,
  scopeCustomerUuid?: string,
  isCustomerScopeReady = true,
  onStatusChange?: (sessionId: string, status?: SessionStatus) => void
): {
  previewResults: (
    filters?: UiPredictionPointFilters,
    modelIdOverride?: string
  ) => void;
  reset: () => void;
  canStartSession: boolean;
  isLoading: boolean;
  session?: Session;
} => {
  const dispatch = useDispatch();

  const [session, setSession] = useState<Session>(INITIAL_STATE);
  const [lastRunProfile, setLastRunProfile] = useState<UnsavedPlantProfile>();
  const [prevSessionStatus, setPrevSessionStatus] = useState<
    | {
        response: GetPredictionPointSessionResponse | undefined;
        error: boolean;
      }
    | undefined
  >(undefined);
  const [prevSessionDone, setPrevSessionDone] = useState(false);

  const resetSession = useCallback(() => {
    setPrevSessionStatus(undefined);
    setPrevSessionDone(false);
    setLastRunProfile(undefined);
    setSession(INITIAL_STATE);
  }, []);

  const { toPredictionPointBaseFilters } =
    useToPredictionPointFilters(scopeCustomerUuid);
  const { data: globalValues } = useQueryPopups(
    portalApi.endpoints.getGlobals.useQuery()
  );
  const globalModelId = globalValues?.plantProfileModelId;
  const canStartSession = Boolean(
    isCustomerScopeReady &&
      plantProfile?.categories.some((c) => c.chipIds.length)
  );
  const [createSession, { isLoading: isCreateSessionLoading }] =
    useMutationPopups(
      portalApi.endpoints.createCategoryCollectionSession.useMutation(),
      {
        success: capitalize(
          t("utils.actions.createdLong", {
            subject: t(
              "components.categoryCollectionProfile.session.session_one"
            ),
          })
        ),
        portalErrorMessagesByCode: {
          [CLIENT_ERROR_CODES.CHIP_IDS_MISSING]: t(
            "components.categoryCollectionProfile.session.chipIdsMissing"
          ),
        },
      }
    );

  const previewResults = useCallback(
    async (filters?: UiPredictionPointFilters, modelIdOverride?: string) => {
      resetSession();
      if (!canStartSession) {
        return;
      }
      const modelId = modelIdOverride || globalModelId || ""; // empty string implies we want veselka to internally decide
      const baseFilters = filters
        ? toPredictionPointBaseFilters(filters)
        : undefined;
      const veselkaPP = plantProfile
        ? toApiCategoryCollectionProfile(plantProfile)
        : undefined;
      const result = await createSession({
        modelId,
        plantProfile: veselkaPP,
        filters: baseFilters,
      });
      if (!result.data) {
        return;
      }
      setSession({
        id: result.data.sessionId,
        error: false,
        stale: false,
        filters: {
          ...baseFilters,
          // Snapshot of Veselka crop IDs expanded at create (`crop_ids`).
          cropIds: isEmpty(result.data.cropIds)
            ? undefined
            : result.data.cropIds,
        },
      });
      setLastRunProfile(plantProfile);
    },
    [
      resetSession,
      canStartSession,
      globalModelId,
      toPredictionPointBaseFilters,
      createSession,
      plantProfile,
    ]
  );

  const isSessionDone = Boolean(
    session.id &&
      session.status &&
      session.status.countOfResults >= session.status.expectedCount
  );

  useEffect(() => {
    if (prevSessionDone !== isSessionDone) {
      setPrevSessionDone(isSessionDone);
      if (isSessionDone && session.id) {
        dispatch(
          addNotification({
            message: t("components.categoryCollectionProfile.session.ready"),
            variant: "success",
          })
        );
      }
    }
  }, [isSessionDone, session.id, prevSessionDone, dispatch]);

  const shouldPoll = session.id && (!session.status || !isSessionDone);
  const {
    data: sessionStatus,
    isLoading: isSessionStatusLoading,
    error: sessionPollingError,
  } = portalApi.endpoints.getCategoryCollectionSession.useQuery(
    shouldPoll && session.id
      ? {
          sessionId: session.id,
          filters: session.filters,
        }
      : skipToken,
    { pollingInterval: SESSION_POLL_INTERVAL_MS }
  );

  const currentSessionState = {
    response: sessionStatus,
    error: Boolean(sessionPollingError),
  };
  if (shouldPoll && !isEqual(prevSessionStatus, currentSessionState)) {
    setPrevSessionStatus(currentSessionState);
    const status = sessionStatus?.sessionStatus;
    if (session.id) {
      onStatusChange?.(session.id, status);
    }
    setSession((prev) => ({
      ...prev,
      status,
      error: Boolean(sessionPollingError),
    }));
  }

  // watches for when the profile results become stale
  useEffect(() => {
    setSession((prev) => ({
      ...prev,
      stale: !arePlantProfilesEqual(plantProfile, lastRunProfile),
    }));
  }, [plantProfile, lastRunProfile, resetSession]);

  return {
    previewResults,
    reset: resetSession,
    canStartSession,
    isLoading: Boolean(
      isCreateSessionLoading ||
        isSessionStatusLoading ||
        (session.id && !isSessionDone)
    ),
    session,
  };
};
